import { GridColDef, GridRowParams } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import { Chip } from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import {
  Modal,
  OverflowTooltip,
  TableNoPagination,
  UsePrivileges,
} from "@4uhub/lib4uhub";
import { format } from "date-fns";
import { ptBR, enUS, es } from "date-fns/locale";
import TocIcon from "@mui/icons-material/Toc";

import IpmTasyModal from "./IpmTasyModal";
import { IIpmTasyLogs } from "../../../../models/ipm-tasy-logs";
import NoPaginationApi from "../../../../services/noPaginationApi.service";
import MacroFilter, { IFilterParams } from "./MacroFilter";

const IPM_X_TASY_ROUTE =
  process.env.REACT_APP_4UBUS +
  "/api/v1/TasyIPMPatientEncounterLog/ListIPMTasy";

const ipmTasyService = new NoPaginationApi<IIpmTasyLogs>(IPM_X_TASY_ROUTE);

const translationPath = "page.4uBus.logs.ipmXTasy.";

const IpmTasyInternal = () => {
  const [open, setOpen] = useState(false);

  const [currentLog, setCurrentLog] = useState<IIpmTasyLogs>();

  const [refresh, setRefresh] = useState(0);

  const today = useMemo(() => new Date(), []);

  const [filterParams, setFilterParams] = useState<IFilterParams>({
    Status: "",
    InitialDate: today,
    EndDate: today,
    SystemOriginCode: "",
    TasyIndividualIdentifier: "",
  });

  const { privileges } = UsePrivileges();

  const permission = !!privileges.find(
    (priv) =>
      priv.domainName === "all" || priv.domainName === "4ubuslogsipmfull",
  );

  const {
    t,
    i18n: { language },
  } = useTranslation();

  const columns: GridColDef<IIpmTasyLogs>[] = [
    {
      field: "status",
      headerName: t(translationPath + "status") || "Status",
      maxWidth: 130,
      renderCell: (params) => {
        return params.value ?? "-";
      },
    },
    {
      field: "messageStatus",
      headerName: t(translationPath + "message_status") || "Message status",
      maxWidth: 180,
      renderCell: (params) => {
        return params.value ? (
          <OverflowTooltip
            text={params.value}
            tooltip={params.value}
            variant="subtitle2"
          />
        ) : (
          "-"
        );
      },
    },
    {
      field: "created",
      headerName: t(translationPath + "created") || "Created",
      maxWidth: 150,
      renderCell: (params) => {
        if (!params.value) return "-";

        const initialDate = new Date(params.value);
        return format(initialDate, "Pp", {
          locale:
            language === "pt-BR" ? ptBR : language === "en-US" ? enUS : es,
        }).replaceAll(",", "");
      },
    },
    {
      field: "tasyIndividualIdentifier",
      headerName:
        t(translationPath + "tasy_individual_identifier") || "Individual code",
      maxWidth: 100,
      renderCell: (params) => {
        return params.value ?? "-";
      },
    },
    {
      field: "systemOriginCode",
      headerName:
        t(translationPath + "service_number") || "Service number (IPM)",
      maxWidth: 220,
      renderCell: (params) => {
        return params.value ?? "-";
      },
    },
    {
      field: "tasyPatientEncounterNumber",
      headerName:
        t(translationPath + "service_number_tasy") || "Service number (Tasy)",
      maxWidth: 230,
      renderCell: (params) => {
        return params.value ?? "-";
      },
    },
    {
      field: "ipmPatientEmrLabExamResultCount",
      headerName: t(translationPath + "lab_exam_quantity") || "Qty Exam Lab",
      maxWidth: 110,
      renderCell: (params) => {
        return params.value ? params.value : 0;
      },
    },
    {
      field: "ipmPatientEmrNonLaboratoryTestCount",
      headerName: t(translationPath + "exam_nlab") || "Exam NLab",
      maxWidth: 100,
      renderCell: (params) => {
        return params.value ? params.value : 0;
      },
    },
    {
      field: "ipmPatientMedicineInUseCount",
      headerName: t(translationPath + "medicines") || "Medicines",
      maxWidth: 115,
      renderCell: (params) => {
        return params.value ? params.value : 0;
      },
    },
    {
      field: "ipmPatientAllergyCount",
      headerName: t(translationPath + "allergy") || "Allergy",
      maxWidth: 80,
      renderCell: (params) => {
        return params.value ? params.value : 0;
      },
    },
    {
      field: "hasPatientClinicalEvolution",
      headerName: t(translationPath + "has_evolution") || "Has evolution",
      maxWidth: 130,
      renderCell: (params) => {
        return (
          <Chip
            size="small"
            label={
              params.value
                ? t(translationPath + "evolution")
                : t(translationPath + "no_evolution")
            }
            color={params.value ? "success" : "error"}
          />
        );
      },
    },
  ];

  const handleFilterChange = useCallback(
    (newFilters: IFilterParams | ((prev: IFilterParams) => IFilterParams)) => {
      setFilterParams(newFilters);
      setRefresh((prev) => prev + 1);
    },
    [],
  );

  const closeModalHandler = () => setOpen(false);

  const openModalHandler = (params: GridRowParams<IIpmTasyLogs>) => {
    setCurrentLog(params.row);
    setOpen(true);
  };

  return (
    <>
      <Modal
        open={open}
        onClose={closeModalHandler}
        title={`${t(translationPath + "logs")}`}
      >
        {currentLog && <IpmTasyModal id={currentLog.id} />}
      </Modal>
      <TableNoPagination<IIpmTasyLogs>
        title={t("components.menu.4ubus.logs.ipmxtasyinternal")}
        columns={columns}
        service={ipmTasyService}
        onRowClick={() => {}}
        showDefaultMacroFilters={false}
        showRowActions={permission}
        headerElements={
          <img
            src="/LGPD.png"
            width={80}
            height={"auto"}
            alt="LPGD guaranteed"
          />
        }
        serviceProps={{
          refresh,
          ...filterParams,
          InitialDate: filterParams?.InitialDate
            ? new Date(filterParams.InitialDate.setHours(15))
                .toISOString()
                .substring(0, 10)
            : undefined,
          EndDate: filterParams?.EndDate
            ? new Date(filterParams.EndDate.setHours(15))
                .toISOString()
                .substring(0, 10)
            : undefined,
        }}
        rowActions={() => {
          return [
            {
              text: t(translationPath + "view_logs"),
              action: openModalHandler,
              icon: <TocIcon />,
            },
          ];
        }}
        macroFilters={() => (
          <MacroFilter
            filterParams={filterParams}
            onFilterParams={handleFilterChange}
          />
        )}
      />
    </>
  );
};

export default IpmTasyInternal;
